import { useEffect, useMemo, useState } from 'react'
import {
  useClientesV2,
  useExecutoresV2,
  useLaboratoriosV2,
  usePlanejadoresV2,
} from '../../hooks/useEmpresas'
import { useEquipamentoList } from '../../hooks/useEquipamentos'
import { useRTsEmpresaList } from '../../hooks/useRTsEmpresa'
import type { PMOC, PMOCCreateInput } from '../../types'
import RTEmpresaModal from '../empresas/RTEmpresaModal'
import formStyles from '../_shared/CrudForm.module.css'

interface Props {
  initial?: PMOC
  onSubmit: (data: PMOCCreateInput) => void
  isLoading?: boolean
  submitLabel?: string
}

export default function PmocForm({ initial, onSubmit, isLoading, submitLabel = 'Salvar' }: Props) {
  const [rtModal, setRtModal] = useState<'planejamento' | 'execucao' | 'laboratorio' | null>(null)
  const [form, setForm] = useState<PMOCCreateInput>(() => ({
    cliente: initial?.cliente ?? '',
    titulo: initial?.titulo ?? '',
    vigencia_inicio: initial?.vigencia_inicio ?? new Date().toISOString().slice(0, 10),
    vigencia_fim: initial?.vigencia_fim ?? '',
    planejador: initial?.planejador ?? null,
    executor: initial?.executor ?? null,
    laboratorio: initial?.laboratorio ?? null,
    rt_planejamento: initial?.rt_planejamento ?? null,
    rt_execucao: initial?.rt_execucao ?? null,
    rt_laboratorio: initial?.rt_laboratorio ?? null,
    numero_art: initial?.numero_art ?? '',
    referencia_normativa:
      initial?.referencia_normativa ??
      'ABNT NBR 13971:2014 e ABNT NBR 17037:2023',
    observacoes_tecnicas: initial?.observacoes_tecnicas ?? '',
    equipamentos_ids: initial?.equipamentos?.map((e) => e.id) ?? [],
  }))

  const clientes = useClientesV2()
  const planejadores = usePlanejadoresV2()
  const executores = useExecutoresV2()
  const laboratorios = useLaboratoriosV2()
  // RTs filtrados pela empresa correspondente — fluxo R7b é validado no backend.
  const rtsPlanejador = useRTsEmpresaList(
    form.planejador ? { empresa: form.planejador } : undefined,
  )
  const rtsExecutor = useRTsEmpresaList(
    form.executor ? { empresa: form.executor } : undefined,
  )
  const rtsLaboratorio = useRTsEmpresaList(
    form.laboratorio ? { empresa: form.laboratorio } : undefined,
  )
  const equipamentos = useEquipamentoList(
    form.cliente ? { empresa: form.cliente, page_size: 500 } : undefined,
  )

  // Limpa equipamentos selecionados se mudar de cliente
  useEffect(() => {
    if (initial && form.cliente === initial.cliente) return
    setForm((f) => ({ ...f, equipamentos_ids: [] }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cliente])

  // Reset RT quando muda a empresa correspondente — RT é por-empresa.
  useEffect(() => {
    setForm((f) => ({ ...f, rt_planejamento: null }))
  }, [form.planejador])
  useEffect(() => {
    setForm((f) => ({ ...f, rt_execucao: null }))
  }, [form.executor])
  useEffect(() => {
    setForm((f) => ({ ...f, rt_laboratorio: null }))
  }, [form.laboratorio])

  function toggleEquipamento(id: string) {
    setForm((f) => {
      const set = new Set(f.equipamentos_ids ?? [])
      if (set.has(id)) set.delete(id)
      else set.add(id)
      return { ...f, equipamentos_ids: Array.from(set) }
    })
  }

  function selecionarTodosEquipamentos() {
    setForm((f) => ({
      ...f,
      equipamentos_ids: equipamentos.data?.results.map((eq) => eq.id) ?? [],
    }))
  }

  const empresaDoRt =
    rtModal === 'planejamento'
      ? planejadores.data?.results.find((e) => e.id === form.planejador)
      : rtModal === 'execucao'
        ? executores.data?.results.find((e) => e.id === form.executor)
        : laboratorios.data?.results.find((e) => e.id === form.laboratorio)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit(form)
  }

  const rtLabel = (rt: { nome: string; conselho: string; uf: string; numero_registro: string; aceite_em?: string | null }) =>
    `${rt.nome} (${rt.conselho}-${rt.uf} ${rt.numero_registro})${rt.aceite_em ? '' : ' — aceite pendente'}`

  const r7bAlerta = useMemo(() => {
    if (!form.laboratorio || !form.planejador) return null
    if (form.laboratorio === form.planejador) {
      return 'R7b: Laboratório QAI deve ser empresa diferente do Planejador (RT mecânico).'
    }
    return null
  }, [form.laboratorio, form.planejador])

  return (
    <form onSubmit={handleSubmit} className={formStyles.form}>
      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Identificação</h2>
        <div className={formStyles.grid2}>
          <div className={formStyles.field}>
            <label htmlFor="cliente">Cliente (contratante) *</label>
            <select
              id="cliente"
              data-testid="pmoc-cliente"
              value={form.cliente}
              onChange={(e) => setForm({ ...form, cliente: e.target.value })}
              disabled={!!initial}
              required
            >
              <option value="">{clientes.isLoading ? 'Carregando...' : '— selecione —'}</option>
              {clientes.data?.results.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razao_social}
                </option>
              ))}
            </select>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="titulo">Título</label>
            <input
              id="titulo"
              data-testid="pmoc-titulo"
              value={form.titulo ?? ''}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="ex.: PMOC 2026 — Sede Indaiatuba"
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="vigencia_inicio">Vigência início *</label>
            <input
              id="vigencia_inicio"
              type="date"
              data-testid="pmoc-vigencia-inicio"
              value={form.vigencia_inicio ?? ''}
              onChange={(e) => setForm({ ...form, vigencia_inicio: e.target.value })}
              required
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="vigencia_fim">Vigência fim</label>
            <input
              id="vigencia_fim"
              type="date"
              data-testid="pmoc-vigencia-fim"
              value={form.vigencia_fim ?? ''}
              onChange={(e) => setForm({ ...form, vigencia_fim: e.target.value })}
            />
          </div>
        </div>
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Atores (NBR 17.037)</h2>
        <div className={formStyles.grid2}>
          <div className={formStyles.field}>
            <label htmlFor="planejador">Planejador (PJ com RT)</label>
            <select
              id="planejador"
              data-testid="pmoc-planejador"
              value={form.planejador ?? ''}
              onChange={(e) => setForm({ ...form, planejador: e.target.value || null })}
            >
              <option value="">— nenhuma —</option>
              {planejadores.data?.results.map((el) => (
                <option key={el.id} value={el.id}>
                  {el.razao_social}
                </option>
              ))}
            </select>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="rt_planejamento">RT Planejamento</label>
            <select
              id="rt_planejamento"
              data-testid="pmoc-rt-planejamento"
              value={form.rt_planejamento ?? ''}
              onChange={(e) =>
                setForm({ ...form, rt_planejamento: e.target.value || null })
              }
              disabled={!form.planejador}
            >
              <option value="">
                {form.planejador ? '— atribuir depois —' : '— selecione planejador primeiro —'}
              </option>
              {rtsPlanejador.data?.results.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rtLabel(rt)}
                </option>
              ))}
            </select>
            {form.planejador && (
              <button type="button" onClick={() => setRtModal('planejamento')} style={{ marginTop: 6 }}>
                + Cadastrar RT desta empresa
              </button>
            )}
          </div>

          <div className={formStyles.field}>
            <label htmlFor="executor">Executor (manutenção mecânica)</label>
            <select
              id="executor"
              data-testid="pmoc-executor"
              value={form.executor ?? ''}
              onChange={(e) => setForm({ ...form, executor: e.target.value || null })}
            >
              <option value="">— nenhuma —</option>
              {executores.data?.results.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.razao_social}
                </option>
              ))}
            </select>
            {form.executor && (
              <button type="button" onClick={() => setRtModal('execucao')} style={{ marginTop: 6 }}>
                + Cadastrar RT desta empresa
              </button>
            )}
          </div>

          <div className={formStyles.field}>
            <label htmlFor="rt_execucao">RT Execução</label>
            <select
              id="rt_execucao"
              data-testid="pmoc-rt-execucao"
              value={form.rt_execucao ?? ''}
              onChange={(e) =>
                setForm({ ...form, rt_execucao: e.target.value || null })
              }
              disabled={!form.executor}
            >
              <option value="">
                {form.executor ? '— atribuir depois —' : '— selecione executor primeiro —'}
              </option>
              {rtsExecutor.data?.results.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rtLabel(rt)}
                </option>
              ))}
            </select>
          </div>

          <div className={formStyles.field}>
            <label htmlFor="laboratorio">Laboratório QAI (opcional)</label>
            <select
              id="laboratorio"
              data-testid="pmoc-laboratorio"
              value={form.laboratorio ?? ''}
              onChange={(e) => setForm({ ...form, laboratorio: e.target.value || null })}
            >
              <option value="">— sem QAI ou ainda a definir —</option>
              {laboratorios.data?.results.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.razao_social}
                </option>
              ))}
            </select>
            {r7bAlerta && (
              <small style={{ color: '#C62828' }} data-testid="r7b-alerta">
                {r7bAlerta}
              </small>
            )}
          </div>

          <div className={formStyles.field}>
            <label htmlFor="rt_laboratorio">RT Laboratório</label>
            <select
              id="rt_laboratorio"
              data-testid="pmoc-rt-laboratorio"
              value={form.rt_laboratorio ?? ''}
              onChange={(e) =>
                setForm({ ...form, rt_laboratorio: e.target.value || null })
              }
              disabled={!form.laboratorio}
            >
              <option value="">
                {form.laboratorio ? '— atribuir depois —' : '— selecione laboratório primeiro —'}
              </option>
              {rtsLaboratorio.data?.results.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rtLabel(rt)}
                </option>
              ))}
            </select>
            {form.laboratorio && (
              <button type="button" onClick={() => setRtModal('laboratorio')} style={{ marginTop: 6 }}>
                + Cadastrar RT desta empresa
              </button>
            )}
          </div>
        </div>
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>Documentação</h2>
        <div className={formStyles.grid2}>
          <div className={formStyles.field}>
            <label htmlFor="numero_art">Número ART/RRT</label>
            <input
              id="numero_art"
              data-testid="pmoc-art"
              value={form.numero_art ?? ''}
              onChange={(e) => setForm({ ...form, numero_art: e.target.value })}
            />
          </div>

          <div className={formStyles.field}>
            <label htmlFor="referencia_normativa">Norma de referência</label>
            <input
              id="referencia_normativa"
              data-testid="pmoc-norma"
              value={form.referencia_normativa ?? ''}
              onChange={(e) =>
                setForm({ ...form, referencia_normativa: e.target.value })
              }
            />
          </div>

          <div className={`${formStyles.field} ${formStyles.span2}`}>
            <label htmlFor="observacoes_tecnicas">Observações técnicas</label>
            <textarea
              id="observacoes_tecnicas"
              data-testid="pmoc-observacoes"
              value={form.observacoes_tecnicas ?? ''}
              onChange={(e) =>
                setForm({ ...form, observacoes_tecnicas: e.target.value })
              }
              rows={3}
            />
          </div>
        </div>
      </section>

      <section className={formStyles.section}>
        <h2 className={formStyles.sectionTitle}>
          Equipamentos vinculados ({form.equipamentos_ids?.length ?? 0})
        </h2>
        {!form.cliente ? (
          <p style={{ color: '#666' }}>Selecione um cliente primeiro.</p>
        ) : equipamentos.isLoading ? (
          <p style={{ color: '#666' }}>Carregando equipamentos...</p>
        ) : (equipamentos.data?.results.length ?? 0) === 0 ? (
          <p style={{ color: '#666' }}>Nenhum equipamento cadastrado para este cliente.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button type="button" onClick={selecionarTodosEquipamentos} data-testid="pmoc-selecionar-todos">
                Selecionar todos ({equipamentos.data?.results.length ?? 0})
              </button>
              <button type="button" onClick={() => setForm((f) => ({ ...f, equipamentos_ids: [] }))}>
                Limpar seleção
              </button>
            </div>
            <div
              data-testid="pmoc-equipamentos"
              style={{
                maxHeight: 280,
                overflow: 'auto',
                border: '1px solid #E0E0E0',
                borderRadius: 4,
                padding: '0.5rem',
              }}
            >
              {equipamentos.data?.results.map((eq) => {
              const checked = form.equipamentos_ids?.includes(eq.id) ?? false
              return (
                <label
                  key={eq.id}
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    padding: '0.4rem',
                    borderBottom: '1px solid #F5F5F5',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleEquipamento(eq.id)}
                  />
                  <span>
                    <strong>
                      {eq.marca} {eq.modelo}
                    </strong>{' '}
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>
                      ({eq.tipo.replace(/_/g, ' ')} • {eq.capacidade_btu?.toLocaleString()} BTU
                      {eq.numero_serie ? ` • S/N ${eq.numero_serie}` : ''})
                    </span>
                  </span>
                </label>
              )
              })}
            </div>
          </>
        )}
      </section>

      <div className={formStyles.actions}>
        <button
          type="submit"
          className={formStyles.btnPrimary}
          disabled={isLoading || !!r7bAlerta}
          data-testid="pmoc-submit"
        >
          {isLoading ? 'Salvando...' : submitLabel}
        </button>
      </div>
      <RTEmpresaModal
        opened={!!rtModal && !!empresaDoRt}
        onClose={() => setRtModal(null)}
        empresaId={empresaDoRt?.id ?? ''}
        empresaNome={empresaDoRt?.razao_social}
        emailSugerido={empresaDoRt?.email}
        onCreated={(rt) => {
          if (rtModal === 'planejamento') setForm((f) => ({ ...f, rt_planejamento: rt.id }))
          if (rtModal === 'execucao') setForm((f) => ({ ...f, rt_execucao: rt.id }))
          if (rtModal === 'laboratorio') setForm((f) => ({ ...f, rt_laboratorio: rt.id }))
        }}
      />
    </form>
  )
}
